import { Application, Container, Texture } from "pixi.js";
import { AssetKeys, loadAllAssets } from "./Assets";
import { Background } from "./Background";
import { BenjiView } from "./BenjiView";
import {
  BENJI_SPRITE_SCALE,
  CAMERA,
  FRAME_DT_MAX,
  MIN_VISIBLE_WORLD_HEIGHT,
  MIN_VISIBLE_WORLD_WIDTH,
  type GameState,
  type Round,
} from "@benji/sim-core";
import { Canopy } from "./Canopy";
import { ParticleSystem } from "./Particles";
import { bakeRotatedTexture } from "./PixiUtils";
import { TopFoliage } from "./TopFoliage";
import { VineFieldView } from "./VineFieldView";

/**
 * Pure rendering shell: PixiJS scene graph + camera + atmosphere. It holds NO
 * game logic or physics — every frame it mirrors an authoritative
 * {@link Round} (`round.benji`, `round.vines()`) onto views.
 */
export class GameScene {
  readonly app = new Application();

  bg!: Background;
  benjiView!: BenjiView;
  canopy!: Canopy;
  topFoliage!: TopFoliage;
  particles!: ParticleSystem;
  vineFieldView!: VineFieldView;
  world!: Container;

  textures: Record<string, Texture> = {};
  scrollX = 0;
  worldScale = 1;
  viewW = 1920;
  viewH = 1080;
  isPortrait = false;

  async init(host: HTMLElement, onFrame: (dt: number) => void) {
    const size = viewportSize();
    await this.app.init({
      width: size.width,
      height: size.height,
      background: 0x0a1f14,
      antialias: true,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true,
    });
    host.appendChild(this.app.canvas);

    this.textures = await loadAllAssets();
    this.textures[AssetKeys.VineRotated] = bakeRotatedTexture(this.app.renderer, this.textures[AssetKeys.Vine]);
    this.buildStage();
    this.resize();

    this.app.ticker.add((t) => {
      onFrame(Math.min(t.deltaMS / 1000, FRAME_DT_MAX));
    });
  }

  resize() {
    const { width: w, height: h } = viewportSize();
    this.app.renderer.resize(w, h);
    this.viewW = w;
    this.viewH = h;
    this.isPortrait = h > w * 1.25;
    this.worldScale = Math.min(w / MIN_VISIBLE_WORLD_WIDTH, h / MIN_VISIBLE_WORLD_HEIGHT);
    this.world.scale.set(this.worldScale);

    this.bg.resize(w, h);
    this.topFoliage.resize(w, h);
    this.canopy.resize(w, h);
    this.benjiView.setDisplayScale(BENJI_SPRITE_SCALE);
  }

  resetCamera() {
    this.scrollX = 0;
    this.world.position.x = 0;
    this.world.position.y = 0;
  }

  /** Mirror the authoritative round onto the scene graph, render-interpolated
   * by `alpha` (leftover fixed-step fraction). `dt` is only the cosmetic pose fade. */
  syncToRound(round: Round, alpha: number, dt: number) {
    this.vineFieldView.sync(round.vines(), alpha);
    this.benjiView.sync(round.benji, alpha, dt);
  }

  updateCamera(dt: number, state: GameState, benjiWorldX: number) {
    const screenX = this.isPortrait ? CAMERA.portraitBenjiScreenXFraction : CAMERA.benjiScreenXFraction;
    const targetScroll = benjiWorldX - (this.viewW * screenX) / this.worldScale;
    const lerp = state === "jumping" ? CAMERA.jumpingLerp : CAMERA.waitingLerp;
    this.scrollX += (targetScroll - this.scrollX) * Math.min(dt * lerp, 1);
    this.world.position.x = -this.scrollX * this.worldScale;
  }

  updateAtmosphere(dt: number, state: GameState) {
    const bgScrollSpeed = CAMERA.bgScroll[state];
    this.bg.update(dt, bgScrollSpeed);
    this.topFoliage.update(dt, bgScrollSpeed * 1.1);
    this.canopy.update(dt, bgScrollSpeed * 1.1);
    this.particles.update(dt);
  }

  screenPoint(worldX: number, worldY: number): { x: number; y: number } {
    return {
      x: this.world.x + worldX * this.worldScale,
      y: this.world.y + worldY * this.worldScale,
    };
  }

  visibleWorldWidth(): number {
    return this.viewW / this.worldScale;
  }

  visibleWorldHeight(): number {
    return this.viewH / this.worldScale;
  }

  private buildStage() {
    this.bg = new Background(this.textures);
    this.app.stage.addChild(this.bg);

    this.world = new Container();
    this.app.stage.addChild(this.world);

    this.vineFieldView = new VineFieldView(this.textures);
    this.world.addChild(this.vineFieldView);

    this.benjiView = new BenjiView(this.textures);
    this.world.addChild(this.benjiView);

    this.topFoliage = new TopFoliage(this.textures);
    this.app.stage.addChild(this.topFoliage);

    this.canopy = new Canopy(this.textures);
    this.app.stage.addChild(this.canopy);

    this.particles = new ParticleSystem(this.textures);
    this.app.stage.addChild(this.particles);
  }
}

function viewportSize(): { width: number; height: number } {
  const vv = window.visualViewport;
  return {
    width: Math.round(vv?.width ?? window.innerWidth),
    height: Math.round(vv?.height ?? window.innerHeight),
  };
}
